import { useNavigate } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { useEffect } from "react";

export default function Privacy() {
  const navigate = useNavigate();
  useEffect(() => {
    document.title = "Privacy Policy — DareDino";
  }, []);
  return (
    <div className="relative min-h-screen bg-[#050505] text-[#E2E8F0]">
      <header className="flex items-center justify-between px-6 md:px-12 py-6 border-b border-[#00F0FF]/20">
        <button
          onClick={() => navigate(-1)}
          className="flex items-center gap-2 text-[#94A3B8] hover:text-white text-xs uppercase tracking-widest"
          data-testid="privacy-back-btn"
        >
          <ArrowLeft size={16} /> Back
        </button>
        <div className="font-heading font-black text-lg text-white tracking-wider">
          DARE<span className="text-[#EAEB18]">DINO</span>{" "}
          <span className="text-[#00F0FF]">/ PRIVACY POLICY</span>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-6 md:px-12 py-12 leading-relaxed" data-testid="privacy-policy-page">
        <div className="text-[10px] uppercase tracking-[0.4em] text-[#00F0FF] mb-3">
          [ LAST UPDATED: 15 FEB 2026 ]
        </div>
        <h1 className="font-heading font-black text-3xl md:text-4xl text-white mb-8">
          Privacy Policy
        </h1>

        <div className="prose-legal space-y-6 text-sm">
          <section>
            <h2 className="font-heading font-bold text-lg text-[#EAEB18] mb-2">1. Who We Are</h2>
            <p>
              DareDino (&quot;DareDino&quot;, &quot;we&quot;, &quot;us&quot;, &quot;our&quot;)
              operates a skill-based endless-runner web game and a paid digital
              add-on called DarePass. This Privacy Policy explains what
              information we collect, how we use it, when we share it, and the
              choices you have.
            </p>
          </section>

          <section>
            <h2 className="font-heading font-bold text-lg text-[#EAEB18] mb-2">
              2. Information We Collect
            </h2>
            <p className="mb-2">
              <strong>a) Google account data (via Google OAuth):</strong> When you sign in
              with Google we receive your <strong>name</strong>,{" "}
              <strong>email address</strong>, and <strong>profile photo URL</strong>.
              We use the name and photo to render your public leaderboard entry
              and — for DarePass members — your unique animated profile frame.
              Your email is used to identify your account and to contact you
              about important service or payment updates only.
            </p>
            <p className="mb-2">
              <strong>b) IP address:</strong> When you access DareDino, we
              read the IP address of the incoming request purely to detect the
              country you are visiting from. This drives our{" "}
              <strong>currency routing</strong>: users in India see prices in
              Indian Rupees (₹) and the domestic Razorpay checkout, while users
              outside India see prices in US Dollars ($) and Razorpay&apos;s
              international checkout. We do not use IP to build advertising
              profiles or to track you across other sites.
            </p>
            <p className="mb-2">
              <strong>c) Gameplay performance data:</strong> Scores, game
              duration, input events (jump / duck timing), and derived
              anti-cheat signatures (HMAC hashes, single-use nonces). This data
              is required to place you on the fair-play leaderboard and to
              detect scripts or bots.
            </p>
            <p>
              <strong>d) Payment metadata:</strong> When you buy DarePass, we
              store the Razorpay order id, payment id, amount, currency, and
              activation timestamp. We <strong>never</strong> receive or store
              your credit card number, CVV, UPI PIN, or netbanking credentials
              — those are collected and encrypted directly by Razorpay on their
              PCI-DSS Level 1 infrastructure.
            </p>
          </section>

          <section>
            <h2 className="font-heading font-bold text-lg text-[#EAEB18] mb-2">
              3. Advertising &amp; Cookies
            </h2>
            <p className="mb-2">
              Free-tier users may see third-party advertisements at natural
              break-points such as after a game-over screen or when creating a
              private room. These ad networks may drop their own cookies on
              your device to serve <strong>personalised advertising</strong>{" "}
              based on your visits to this and other websites.
            </p>
            <p>
              <strong>DarePass members do not see any ads.</strong> The moment
              your DarePass subscription becomes active, all third-party ad
              scripts are completely <strong>blocked from loading</strong> and
              the associated ad cookies are not set. This is one of the core
              benefits of upgrading and it is enforced client-side and
              server-side (the ad-view endpoints simply refuse to run for pro
              accounts).
            </p>
          </section>

          <section>
            <h2 className="font-heading font-bold text-lg text-[#EAEB18] mb-2">
              4. Payment Data &amp; Razorpay
            </h2>
            <p>
              All DarePass transactions are handled by{" "}
              <strong>Razorpay Software Pvt. Ltd.</strong> — a PCI-DSS Level 1
              certified payment processor. Payment credentials (card numbers,
              UPI IDs entered at checkout, netbanking usernames, wallet PINs)
              are transmitted directly from your browser to Razorpay over TLS
              and are <strong>never stored on the DareDino database</strong>.
              We only retain the non-sensitive references Razorpay returns
              after a successful charge (order id, payment id, signature) so we
              can verify entitlement and issue refunds when applicable.
            </p>
          </section>

          <section>
            <h2 className="font-heading font-bold text-lg text-[#EAEB18] mb-2">
              5. How We Use Your Information
            </h2>
            <ul className="list-disc pl-6 space-y-1">
              <li>Authenticate you and keep your session active for 7 days.</li>
              <li>Display your name and photo on public leaderboards and inside the DarePass premium profile frame.</li>
              <li>Detect and reject cheating (HMAC score signing, replay analysis, velocity checks).</li>
              <li>Route you to the correct pricing and payment flow based on your country.</li>
              <li>Send you critical service, security, or payment-related emails.</li>
            </ul>
          </section>

          <section>
            <h2 className="font-heading font-bold text-lg text-[#EAEB18] mb-2">6. Data Sharing</h2>
            <p>We do <strong>not</strong> sell your personal data. Limited data is shared only with:</p>
            <ul className="list-disc pl-6 space-y-1">
              <li><strong>Google</strong> — OAuth sign-in only.</li>
              <li><strong>Razorpay</strong> — payment processing for DarePass.</li>
              <li><strong>ipapi.co / Cloudflare</strong> — country lookup by IP for currency routing (an IP is sent, no other identifiers).</li>
              <li><strong>Advertising networks</strong> — only for free-tier users, and only for the ad-network cookies they set themselves. DarePass members are exempt.</li>
              <li>Law enforcement, only when we receive a valid legal order.</li>
            </ul>
          </section>

          <section>
            <h2 className="font-heading font-bold text-lg text-[#EAEB18] mb-2">7. Public Information</h2>
            <p>
              Your <strong>display name</strong>, <strong>profile picture</strong>, and{" "}
              <strong>game scores</strong> are visible on public leaderboards
              and inside private rooms you join. Your email, IP address, and
              payment references are never shown publicly.
            </p>
          </section>

          <section>
            <h2 className="font-heading font-bold text-lg text-[#EAEB18] mb-2">8. Data Retention</h2>
            <p>
              Game history, session logs, and playtime records older than{" "}
              <strong>28 days</strong> are automatically purged. Your account
              profile, personal-best score, and active DarePass subscription
              are kept until you request deletion.
            </p>
          </section>

          <section>
            <h2 className="font-heading font-bold text-lg text-[#EAEB18] mb-2">9. Security</h2>
            <p>
              All traffic is served over HTTPS. Sessions use cryptographically
              random 256-bit tokens delivered as HttpOnly cookies. Score
              submissions are protected by HMAC-SHA256 signatures with a
              single-use server-issued nonce. We routinely rotate secrets and
              patch dependencies. Please report vulnerabilities to{" "}
              <a href="mailto:security@daredino.com" className="text-[#00F0FF] underline">
                security@daredino.com
              </a>
              .
            </p>
          </section>

          <section>
            <h2 className="font-heading font-bold text-lg text-[#EAEB18] mb-2">10. Your Rights</h2>
            <ul className="list-disc pl-6 space-y-1">
              <li>Sign out at any time — this destroys your server-side session.</li>
              <li>Request a copy of your data (email <a href="mailto:privacy@daredino.com" className="text-[#00F0FF] underline">privacy@daredino.com</a>).</li>
              <li>Request account deletion and full data erasure.</li>
              <li>Cancel DarePass — your subscription remains active until the paid period ends, then auto-expires.</li>
            </ul>
          </section>

          <section>
            <h2 className="font-heading font-bold text-lg text-[#EAEB18] mb-2">11. Children</h2>
            <p>
              DareDino is not intended for users under 18. If you believe a
              minor has created an account, contact us and we will delete it.
            </p>
          </section>

          <section>
            <h2 className="font-heading font-bold text-lg text-[#EAEB18] mb-2">12. Changes</h2>
            <p>
              We may update this policy from time to time. Material changes
              will be announced on the homepage and the &quot;Last Updated&quot;
              date above will change.
            </p>
          </section>

          <section>
            <h2 className="font-heading font-bold text-lg text-[#EAEB18] mb-2">13. Contact</h2>
            <p>
              Privacy questions:{" "}
              <a href="mailto:privacy@daredino.com" className="text-[#00F0FF] underline">
                privacy@daredino.com
              </a>
            </p>
          </section>
        </div>
      </main>
    </div>
  );
}
